interface ReactNativeWebView {
  postMessage: (message: string) => void;
}

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebView;
    /** 네이티브 앱 → 웹 메시지 수신 콜백 */
    onNativeMessage?: (messageJson: string) => void;
  }
}

/**
 * React Native WebView로 메시지를 전송한다.
 */
export function sendToNative(message: string): boolean {
  try {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(message);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * 현재 환경이 React Native WebView인지 확인한다.
 */
/**
 * 네이티브 앱에 햅틱 피드백을 요청한다.
 */
export function triggerHaptic(): boolean {
  return sendToNative(JSON.stringify({ type: 'HAPTIC_FEEDBACK' }));
}

/**
 * 현재 환경이 React Native WebView인지 확인한다.
 */
export function isWebviewEnvironment(): boolean {
  return !!window.ReactNativeWebView;
}

/**
 * React Native WebView에서 전송하는 메시지 타입
 */
export interface NativeMessage {
  type: string;
  payload?: { data?: string };
}

type NativeMessageHandler = (message: NativeMessage) => void;

const listeners = new Set<NativeMessageHandler>();

function dispatch(raw: unknown) {
  try {
    const data =
      typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (data && typeof data.type === 'string') {
      console.log('[Webview] dispatch native message:', data);
      listeners.forEach((fn) => fn(data as NativeMessage));
    }
  } catch {
    // 파싱 실패 무시
  }
}

// ── 수신 채널 초기화 (한 번만 실행) ──
let initialized = false;

function ensureInitialized() {
  if (initialized) return;
  initialized = true;

  // 1) window.onNativeMessage — 네이티브에서 injectJavaScript로 호출
  //    예: webViewRef.injectJavaScript(`window.onNativeMessage('${json}')`)
  window.onNativeMessage = (messageJson: string) => {
    console.log('[Webview] onNativeMessage called:', messageJson);
    dispatch(messageJson);
  };

  // 2) window 'message' 이벤트 — RN WebView postMessage 또는 iframe 통신
  window.addEventListener('message', (event: MessageEvent) => {
    dispatch(event.data);
  });

  // 3) document 'message' 이벤트 — Android WebView 일부 버전
  document.addEventListener('message', ((event: Event) => {
    dispatch((event as MessageEvent).data);
  }) as EventListener);

  console.log('[Webview] Native message listeners initialized');
}

/**
 * 네이티브 메시지 리스너를 등록한다.
 * 여러 채널(onNativeMessage, window message, document message)을 모두 감시한다.
 */
export function addNativeMessageListener(
  handler: NativeMessageHandler
): () => void {
  ensureInitialized();
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}
